import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const Leaderboard = ({ kpis, onSelectAgent, selectedAgentName }) => {
  const [sortConfig, setSortConfig] = useState({
    key: "baseCommission",
    direction: "desc",
  });
  const [isTableOpen, setIsTableOpen] = useState(false);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    })
      .format(val)
      .replace("€", "€ ");
  };

  const sortedKPIs = useMemo(() => {
    const sortableItems = [...kpis];
    sortableItems.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    return sortableItems;
  }, [kpis, sortConfig]);

  const requestSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const handleExportCSV = () => {
    const headers = [
      "Collaboratore",
      "Provvigioni",
      "Bonus Sconto",
      "Netto Motor",
      "Polizze",
      "Media Gar",
      "Mix No-Motor",
      "Retention %",
    ];
    const rows = sortedKPIs.map((a) => [
      a.name,
      a.baseCommission.toFixed(2),
      a.totalBonusSconto.toFixed(2),
      a.nettoMotor.toFixed(2),
      a.polizzeStrict,
      a.avgGarMotorTotal.toFixed(2),
      a.mixNonMotor.toFixed(2) + "%",
      a.retentionRate.toFixed(2) + "%",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((r) => r.join(";")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `report_collaboratori_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: 1,
        borderColor: "grey.100",
        boxShadow: 1,
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: 1,
          borderColor: "grey.100",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => setIsTableOpen(!isTableOpen)}
            size="small"
            sx={{ color: "text.primary" }}
          >
            {isTableOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              Dettaglio Completo Collaboratori
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: "medium" }}
            >
              Conteggio polizze core (Nuove/Rinnovi) ed esclusione riferimenti
              con asterisco (*).
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleExportCSV}
            startIcon={<InsertDriveFileIcon sx={{ color: "info.main" }} />}
            sx={{
              fontSize: "0.625rem",
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              borderColor: "grey.200",
              color: "text.secondary",
              "&:hover": { bgcolor: "grey.50" },
            }}
          >
            Esporta CSV
          </Button>
        </Box>
      </Box>
      <Collapse in={isTableOpen} timeout="auto" unmountOnExit>
        <TableContainer
          sx={{
            maxHeight: "400px",
            overflowY: "scroll",
            scrollbarWidth: "none",
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="leaderboard table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    py: 2.5,
                    px: 3,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "info.main",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    whiteSpace: "nowrap",
                    bgcolor: "info.50",
                    zIndex: 10,
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={
                      sortConfig.key === "name" ? sortConfig.direction : "asc"
                    }
                    onClick={() => requestSort("name")}
                  >
                    Collaboratore
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "success.main",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                    bgcolor: "success.50",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "baseCommission"}
                    direction={
                      sortConfig.key === "baseCommission"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("baseCommission")}
                  >
                    Provvigioni
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "warning.main",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                    bgcolor: "warning.50",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "totalBonusSconto"}
                    direction={
                      sortConfig.key === "totalBonusSconto"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("totalBonusSconto")}
                  >
                    BONUS SCONTO
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "error.main",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "nettoMotor"}
                    direction={
                      sortConfig.key === "nettoMotor"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("nettoMotor")}
                  >
                    Netto Motor
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "polizzeStrict"}
                    direction={
                      sortConfig.key === "polizzeStrict"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("polizzeStrict")}
                  >
                    # Polizze (N+R)
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  Qualità (Motor)*
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "info.dark",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "avgGarMotorTotal"}
                    direction={
                      sortConfig.key === "avgGarMotorTotal"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("avgGarMotorTotal")}
                  >
                    Media Gar.
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: 2.5,
                    px: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "primary.main",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "mixNonMotor"}
                    direction={
                      sortConfig.key === "mixNonMotor"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("mixNonMotor")}
                  >
                    Mix No-M
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    py: 2.5,
                    px: 3,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "secondary.main",
                    textTransform: "uppercase",
                    letterSpacing: "-0.05em",
                  }}
                >
                  <TableSortLabel
                    active={sortConfig.key === "retentionRate"}
                    direction={
                      sortConfig.key === "retentionRate"
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => requestSort("retentionRate")}
                  >
                    Retention %
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedKPIs.map((agent) => (
                <TableRow
                  key={agent.name}
                  onClick={() => onSelectAgent(agent.name)}
                  hover
                  sx={{
                    cursor: "pointer",
                    bgcolor:
                      selectedAgentName === agent.name ? "info.50" : "inherit",
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell
                    sx={{
                      px: 3,
                      py: 2.5,
                      fontWeight: 900,
                      color: "text.primary",
                      fontSize: "0.75rem",
                      whiteSpace: "wrap",
                      bgcolor: "info.50",
                      left: 0,
                      zIndex: 10,
                    }}
                  >
                    {agent.name}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: 2, py: 2.5, bgcolor: "success.50" }}
                  >
                    <Typography
                      sx={{
                        color: "success.dark",
                        fontWeight: 900,
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(agent.baseCommission)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: 2, py: 2.5, bgcolor: "warning.50" }}
                  >
                    <Typography
                      sx={{
                        color: "warning.dark",
                        fontWeight: 900,
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(agent.totalBonusSconto)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 2, py: 2.5 }}>
                    <Typography
                      sx={{
                        color: "error.main",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(agent.nettoMotor)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 2, py: 2.5 }}>
                    <Typography
                      sx={{
                        color: "text.primary",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}
                    >
                      {agent.polizzeStrict}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 2, py: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 900,
                            color: "error.light",
                          }}
                        >
                          0G
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color: "text.secondary",
                          }}
                        >
                          {agent.count0Gar}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 900,
                            color: "warning.light",
                          }}
                        >
                          1G
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color: "text.secondary",
                          }}
                        >
                          {agent.count1Gar}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 900,
                            color: "success.light",
                          }}
                        >
                          2+G
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            color: "text.secondary",
                          }}
                        >
                          {agent.count2PlusGar}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 2, py: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "info.dark",
                          fontWeight: 900,
                          fontSize: "0.875rem",
                        }}
                      >
                        {agent.avgGarMotorTotal.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.5625rem",
                          color: "text.disabled",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          letterSpacing: "-0.05em",
                        }}
                      >
                        N: {agent.avgGarMotorNew.toFixed(1)} R:{" "}
                        {agent.avgGarMotorRen.toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 2, py: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 900,
                        color: agent.reqCross ? "success.main" : "primary.main",
                      }}
                    >
                      {agent.mixNonMotor.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ px: 3, py: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 900,
                        color:
                          agent.retentionRate >= 80
                            ? "success.main"
                            : agent.retentionRate >= 70
                            ? "info.main"
                            : "error.main",
                      }}
                    >
                      {agent.retentionRate > 0
                        ? `${agent.retentionRate.toFixed(1)}%`
                        : "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            p: 2,
            fontSize: "0.5625rem",
            color: "text.disabled",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            textAlign: "center",
            borderTop: 1,
            borderColor: "grey.100",
          }}
        >
          * Qualità basata solo su Emissioni Annuali/Mensili/Semestrali. Esclusi
          Prodotti Casa/Infortuni e polizze con *
        </Box>
      </Collapse>
    </Paper>
  );
};

export default Leaderboard;
